import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { localStorage as storage } from "@/lib/localStorage";

// ✅ Create QueryClient OUTSIDE the try block so it is accessible everywhere
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,                // Data is fresh for 0ms
      gcTime: 1000 * 60 * 60 * 24, // Garbage collect after 24 hours
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// ✅ Create persister instance
const persister = createAsyncStoragePersister({
  storage: window.localStorage,
  key: "reactQuery",
});

// ✅ Pre-boot seeding logic (unchanged except scoping fix)
try {
  const url = new URL(window.location.href);
  const forceSeed = url.searchParams.get("seed") === "1" || url.hash.includes("seed");
  const existingRaw = window.localStorage.getItem("cm:v1");
  const autoSeedFlag = window.localStorage.getItem("cm:autoseed");

  let shouldSeed = forceSeed;

  if (!shouldSeed) {
    if (!existingRaw) {
      shouldSeed = true;
    } else {
      try {
        const existing = JSON.parse(existingRaw);
        const emptyTypes = Array.isArray(existing?.types) ? existing.types.length === 0 : true;
        const emptyCodes = Array.isArray(existing?.codes) ? existing.codes.length === 0 : true;
        if (emptyTypes && emptyCodes) {
          shouldSeed = true;
        }
      } catch {
        shouldSeed = true;
      }
    }
  }

  if (shouldSeed) {
    storage.reset();
    storage.seedData();
    window.localStorage.setItem("cm:autoseed", "1");
  }
} catch (e) {
  console.error("Pre-boot auto-seed failed:", e);
}


createRoot(document.getElementById("root")!).render(
  <PersistQueryClientProvider
    client={queryClient}
    persistOptions={{
      persister,
      maxAge: 24 * 60 * 60 * 1000, // 24 hours
    }}
  >
    <App />
  </PersistQueryClientProvider>
);
